/**
 * Code-Stand: ThreeJS r179
 * Das folgende ist eine eigene Implementierung, Interface-kompatibel mit
 * {@link LoadingManager}
 */

export class MyLoadingManager {
  // Öffentliche Flags/Zähler (von außen beschreibbar, wie bei dir)
  isLoading = false;
  itemsLoaded = 0;
  itemsError = 0;
  itemsTotal = 0;

  // URL-Modifikation
  urlModifier = null;

  // Callbacks analog Three.js (onStart NICHT im ctor gesetzt)
  onStart = undefined;
  onLoad = undefined;
  onProgress = undefined;
  onError = undefined;

  // NEU: Abort-Controller wie im aktuellen Three.js
  abortController = new AbortController();

  // NEU: Handler-Registry (regex, loader) Paare
  _handlers = [];

  /**
   * Optionaler ctor wie bei Three.js (alles optional).
   * Bestehender Code ohne ctor-Argumente funktioniert weiter.
   */
  constructor(onLoad, onProgress, onError) {
    if (onLoad) this.onLoad = onLoad;
    if (onProgress) this.onProgress = onProgress;
    if (onError) this.onError = onError;
  }

  /**
   * Muss von Loadern beim Start aufgerufen werden.
   * Entspricht Three.js: itemsTotal++ und ggf. onStart()
   */
  itemStart(url) {
    this.itemsTotal++;

    if (this.isLoading === false && typeof this.onStart === "function") {
      this.onStart(url, this.itemsLoaded, this.itemsTotal);
    }

    this.isLoading = true;
  }

  /**
   * Muss von Loadern beim erfolgreichen Ende aufgerufen werden.
   * Entspricht Three.js: itemsLoaded++, onProgress(), ggf. onLoad()
   * + DEINS: checkLoaded() (inkl. triggerLoadedDebounced)
   */
  itemEnd(url) {
    this.itemsLoaded++;

    if (typeof this.onProgress === "function") {
      this.onProgress(url, this.itemsLoaded, this.itemsTotal);
    }

    if (this.itemsLoaded === this.itemsTotal) {
      this.isLoading = false;

      if (typeof this.onLoad === "function") {
        this.onLoad();
      }
    }

    // Deine bestehende Logik beibehalten
    this.checkLoaded();
  }

  /**
   * Muss von Loadern beim Fehler aufgerufen werden.
   * Entspricht Three.js: onError()
   * + DEINS: itemsError++ und checkLoaded()
   */
  itemError(url) {
    if (typeof this.onError === "function") {
      this.onError(url);
    }

    this.itemsError++;
    this.checkLoaded();
  }

  /**
   * Deine bestehende "alles erledigt"-Prüfung
   * (zählt auch Fehler als "erledigt")
   */
  checkLoaded() {
    if (this.itemsLoaded + this.itemsError < this.itemsTotal) return;

    this.isLoading = false;

    // Wie gehabt – extern definiert in deinem Projekt
    try {
      if (typeof triggerLoadedDebounced === "function") {
        triggerLoadedDebounced();
      }
    } catch (e) {
      // still und leise ignorieren, falls nicht vorhanden
    }
  }

  /**
   * URL-Auflösung mit optionalem Modifier (wie Three.js)
   */
  resolveURL(url) {
    if (this.urlModifier) {
      return this.urlModifier(url);
    }
    return url;
  }

  /**
   * URL-Modifier registrieren (wie Three.js)
   */
  setURLModifier(transform) {
    this.urlModifier = transform;
    return this;
  }

  /**
   * NEU: Loader-Handler-API wie in Three.js
   */
  addHandler(regex, loader) {
    this._handlers.push(regex, loader);
    return this;
  }

  removeHandler(regex) {
    const index = this._handlers.indexOf(regex);
    if (index !== -1) {
      this._handlers.splice(index, 2);
    }
    return this;
  }

  getHandler(file) {
    for (let i = 0, l = this._handlers.length; i < l; i += 2) {
      const regex = this._handlers[i];
      const loader = this._handlers[i + 1];

      if (regex.global) regex.lastIndex = 0; // wie Three.js (#17920)
      if (regex.test(file)) return loader;
    }
    return null;
  }

  /**
   * NEU: Ladevorgänge abbrechen (setzt neues AbortController-Objekt)
   * Wirkt nur, wenn verwendete Loader das unterstützen.
   */
  abort() {
    this.abortController.abort();
    this.abortController = new AbortController();
    return this;
  }

  /**
   * Dein Reset, leicht erweitert: setzt auch den AbortController neu.
   * (Handlers & Callbacks bleiben absichtlich erhalten)
   */
  reset() {
    this.isLoading = false;
    this.itemsLoaded = 0;
    this.itemsError = 0;
    this.itemsTotal = 0;
    this.urlModifier = null;

    this.abortController = new AbortController();
  }
}
